import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useMemo, useState } from 'react';
import { SEBS_COMPOSER_EXPERIMENT } from '~/constants/composer';
import {
  browserCookieIdExperiments,
  clientSideUserIdExperiments,
  userIdExperiments
} from '~/constants/home/experiments';
import {
  useIsLoggedIn,
  useUserIsImpersonating
} from '~/context/common/UserContext';
import {
  useComposerAssignedBucket,
  useShowComposerTreatment
} from '~/context/composer/ComposerContext';
import { useGetIsReturnUser } from '~/context/home/GlobalContext';
import useEventExperiments from '~/hooks/common/telemetry/useEventExperiments';
import useSendFeatureBranchToDataDog from '~/hooks/common/telemetry/useSendFeatureBranchToDataDog';
import useSendNativeDeviceTypeToDataDog from '~/hooks/common/telemetry/useSendNativeDeviceTypeToDataDog';

const useSetDataDogContext = () => {
  const composerAssignedBucket = useComposerAssignedBucket();
  const showComposerTreatment = useShowComposerTreatment();
  const isImpersonating = useUserIsImpersonating();
  const isLoggedIn = useIsLoggedIn();
  const isReturningUser = useGetIsReturnUser();
  // Server-side user-id experiments are only preloaded if logged in at the
  // initial request, so freeze this at mount to avoid waiting on it after a
  // later native auth sync.
  const [wasLoggedInAtMount] = useState(isLoggedIn);
  const composerExperiments = useMemo(
    () => ({ [SEBS_COMPOSER_EXPERIMENT]: composerAssignedBucket }),
    [composerAssignedBucket]
  );

  useEventExperiments(
    browserCookieIdExperiments,
    wasLoggedInAtMount ? userIdExperiments : clientSideUserIdExperiments,
    composerExperiments
  );
  useSendFeatureBranchToDataDog();
  useSendNativeDeviceTypeToDataDog();

  useEffect(() => {
    datadogRum.setGlobalContextProperty('isImpersonating', isImpersonating);
    datadogRum.setGlobalContextProperty('isReturningUser', isReturningUser);
    datadogRum.setGlobalContextProperty(
      'showComposerTreatment',
      showComposerTreatment
    );
  }, [isImpersonating, isReturningUser, showComposerTreatment]);
};

export default useSetDataDogContext;
